/**
 * Configuration front du système XP / Étoiles / Flamme.
 *
 * ⚠️ Le back est la source de vérité : il calcule l'XP réelle, les niveaux et les
 * déblocages. Le front NE calcule PAS l'XP. Les constantes ci-dessous servent
 * uniquement à l'AFFICHAGE immédiat (feedback Duolingo par réponse), réconcilié
 * par le total renvoyé par GET /profils-xp/me/ en fin de quiz.
 */

// Estimé d'affichage par réponse (formule doc : juste = 10×1.8 = 18, faux = 3×1.8 ≈ 5).
// Purement cosmétique — aucune incidence sur l'XP réelle (créditée par le back à la soumission).
export const XP_BONNE = 18;
export const XP_MAUVAIS = 5;
// Bonne réponse APRÈS un coup de pouce : rapporte moins (arbitrage pédagogique).
// ⚠️ Affichage seul en V1 — le back devra appliquer la même règle au crédit réel.
export const XP_BONNE_AIDE = 12;

// ── Barème DÉFIS QUOTIDIENS (source='quiz_auto', contrat back du 12/07) ──
// SANS le ×1,8 réservé au quiz prof (qui doit rester l'activité la plus
// rémunératrice). Aide = 7 (même ratio ⅔ que 12/18). Défi parfait = 50 XP.
// Synchronisé avec le barème du back (endpoint /defis-quotidiens/submit/).
export const XP_BONNE_AUTO = 10;
export const XP_BONNE_AIDE_AUTO = 7;
export const XP_EFFORT_AUTO = 3;
// Quota de défis CRÉDITÉS par jour — le back est juge (contrainte DB) ;
// le front affiche le badge x/5 depuis profil.defis_credites_jour.
export const DEFIS_QUOTA_JOUR = 5;
// Plafond de défis JOUABLES par jour (garde-fou bien-être / temps d'écran,
// aligné sur le plafond de stockage back). À 30 : le front bloque le
// lancement avec un message « victoire » — profil.defis_total_jour (serveur)
// prime, compteur localStorage en repli mono-appareil.
export const DEFIS_MAX_JOUR = 30;

// Skin de plumage par défaut (contrat figé partagé avec le moteur tano()).
export const PLUME_DEFAUT = 'foret';

/**
 * Métadonnées d'AFFICHAGE des skins (libellés + indice de seuil pour les cadenas
 * dans la garde-robe). ⚠️ NE sert PAS à débloquer : le déblocage est lu depuis
 * `skins_debloques` renvoyé par l'API. Les seuils ci-dessous sont indicatifs et
 * configurables côté back — À COMPLÉTER/AJUSTER quand les skins/paliers définitifs
 * seront fournis.
 *
 * Les `id` (foret, bronze, …) sont un CONTRAT FIGÉ avec le moteur tano().
 */
export const SKINS_META = {
  // `seuil` : valeur numérique INDICATIVE (même métrique que la lane : XP, ⭐ ou 🔥)
  // pour l'étiquette « encore X » de la garde-robe. Affichage seul, jamais un déblocage.
  // `halo` : la couleur dominante du skin, seule chose qu'un skin VERROUILLÉ laisse
  // voir (silhouette + halo, ou flou coloré pour le prochain à portée) : on devine,
  // on ne voit pas (décision prof 2026-09-14, la curiosité avant la révélation).
  plumage: {
    // Label robot (l'id `foret` reste : contrat figé back/moteur)
    foret: { label: 'Néon', seuilTexte: 'Départ', seuil: 0, halo: '#29B6E8' },
    bronze: { label: 'Bronze', seuilTexte: '250 XP', seuil: 250, halo: '#CD7F32' },
    argent: { label: 'Argent', seuilTexte: '750 XP', seuil: 750, halo: '#C8D0DC' },
    or: { label: 'Or', seuilTexte: '2000 XP', seuil: 2000, rare: true, halo: '#FFB938' },
    cosmos: { label: 'Cosmos', seuilTexte: '5000 XP', seuil: 5000, rare: true, halo: '#A78BFA' },
  },
  accessoire: {
    lunettes: { label: 'Lunettes', seuilTexte: '10 ⭐', seuil: 10, halo: '#29B6E8' },
    echarpe: { label: 'Écharpe', seuilTexte: '25 ⭐', seuil: 25, halo: '#F87171' },
    chapeau: { label: 'Chapeau créole', seuilTexte: '50 ⭐', seuil: 50, halo: '#F59E0B' },
    // Label robot : l'ancien casque est devenu le tuit-tuit compagnon
    // (l'ancienne mascotte perchée — id API `casque` conservé)
    casque: { label: 'Tuit-tuit', seuilTexte: '100 ⭐', seuil: 100, rare: true, halo: '#4ADE80' },
    couronne: { label: 'Couronne d’assidu', seuilTexte: '200 ⭐', seuil: 200, rare: true, halo: '#FFB938' },
  },
  aura: {
    etincelle: { label: 'Étincelle', seuilTexte: '3 🔥', seuil: 3, halo: '#FDE047' },
    flamme: { label: 'Flamme', seuilTexte: '7 🔥', seuil: 7, halo: '#F97316' },
    halo: { label: 'Halo ardent', seuilTexte: '12 🔥', seuil: 12, halo: '#FB923C' },
    anneau: { label: 'Anneau de feu', seuilTexte: '16 🔥', seuil: 16, rare: true, halo: '#EF4444' },
    // Label robot : l'aura `braise` est devenue des flammes sous les pieds
    braise: { label: 'Réacteurs', seuilTexte: '20 🔥', seuil: 20, rare: true, halo: '#DC2626' },
  },
};

// Métrique de progression d'une lane de skins (même unité que `seuil`).
export const metriqueLane = (profil, type) => {
  if (!profil) return 0;
  if (type === 'plumage') return profil.xp_total ?? 0;
  if (type === 'accessoire') return profil.etoiles_total ?? 0;
  return profil.flamme_jours ?? 0;
};

export const UNITE_LANE = { plumage: 'XP', accessoire: '⭐', aura: '🔥' };

// Ordre + métadonnées d'affichage des lanes de la garde-robe (maquette V3).
export const SKIN_CATEGORIES = [
  {
    type: 'plumage', titre: 'XP — l\'armure', icon: '📊', laneClass: 'xp', thColor: '#8Fd6f5',
    tag: 'volume · permanent', sousTitre: 'Néon → Bronze → Argent → Or → Cosmos. On lit la montée en grade.',
  },
  {
    type: 'accessoire', titre: 'Étoiles — les accessoires', icon: '⭐', laneClass: 'star', thColor: '#FFD27A',
    tag: 'assiduité · permanent', sousTitre: '1 étoile = 1 jour de présence. Se collectionne avec la fidélité.',
  },
  {
    type: 'aura', titre: 'Flamme — les auras', icon: '🔥', laneClass: 'flame', thColor: '#FBC19A',
    tag: 'régularité · max 20', sousTitre: 'Jours consécutifs. Un halo autour de Tano.',
  },
];

// Ratio de remplissage de la barre de progression vers le niveau suivant (0..1).
export const progressionRatio = (profil) => {
  const p = profil?.progression;
  if (!p) return 0;
  const span = (p.xp_seuil_suivant ?? 0) - (p.xp_seuil_actuel ?? 0);
  if (span <= 0) return 0;
  const done = (profil.xp_total ?? 0) - (p.xp_seuil_actuel ?? 0);
  return Math.max(0, Math.min(1, done / span));
};
